import { useMemo } from "react";
import styled from "styled-components";
import {
  ArrowRightIcon,
  MegaphoneIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@sablier/v2-components/molecules";
import { links } from "@sablier/v2-constants";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { useModalTransaction } from "~/client/hooks";
import type { MouseEvent } from "react";

const Wrapper = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    width: 100%;
    margin-top: calc(${(props) => props.theme.sizes.edge} * 2);
    padding: calc(${(props) => props.theme.sizes.edge} * 1) 0;
    border-top: 2px solid ${(props) => props.theme.colors.border};
    &:empty {
      min-height: calc(${(props) => props.theme.sizes.edge} * 1);
      padding: 0;
      border: none;
    }
  }
`;

const Actions = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    justify-content: center;
    width: 100%;
    column-gap: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
    & > * {
      flex: 1;
      &:only-child {
        max-width: 300px;
      }
    }
  }
`;

function Footer() {
  const { data, doClose } = useModalTransaction();
  const { t } = useT();

  const buttons = useMemo(
    () =>
      data.footer?.map((button) => ({
        ...button,
        onClick: (event: MouseEvent<HTMLElement>) => {
          if (button?.onClick) {
            button.onClick(event, doClose);
          } else {
            /**
             * For `ctrl-click` (open in new tab) and internal transitions, don't close the modal.
             * For the latter, closing will be handled by the app-level watcher.
             */
            if (!event.metaKey && button.purpose !== "internal") {
              doClose();
            }
          }
        },
      })) || [],
    [data.footer, doClose],
  );

  const isReportActive = useMemo(
    () => ["fail"].includes(data.status),
    [data.status],
  );

  const isCloseActive = useMemo(
    () => data.isNotClosable !== true,
    [data.isNotClosable],
  );

  const isEmpty = useMemo(
    () => !buttons.length && !isReportActive && !isCloseActive,
    [isCloseActive, isReportActive, buttons],
  );

  const count = useMemo(() => {
    return (isCloseActive ? 1 : 0) + (isReportActive ? 1 : 0) + buttons.length;
  }, [buttons, isCloseActive, isReportActive]);

  return (
    <Wrapper>
      {!isEmpty && (
        <Actions>
          {isCloseActive && (
            <Button
              accent={"iconic"}
              appearance={"outline"}
              onClick={doClose}
              right={XMarkIcon}
              title={t("words.close")}
              isDisabled={data.isNotClosable === "preview"}
            />
          )}

          {buttons.map((button, index) => (
            <Button
              key={index}
              accent={"iconic"}
              appearance={"outline"}
              purpose={"internal"}
              right={ArrowRightIcon}
              {...button}
            />
          ))}

          {isReportActive && (
            <Button
              accent={"failLight"}
              appearance={"outline"}
              to={links.discord}
              right={MegaphoneIcon}
              title={count > 2 ? t("words.report") : t("structs.reportProblem")}
              purpose={"external"}
            />
          )}
        </Actions>
      )}
    </Wrapper>
  );
}

export default Footer;
